import React, { useEffect, useState } from "react";
import useAPIError from "../../APIError";
import NmefSpinner from "../..//NmefSpinner";
import AssetContext from "../../Assets/AssetContext";
import Asset from "../../Assets";
import { FeeDataAPI } from '../../API/FeeDataAPI';
import { ApplicationDataAPI } from '../../API/ApplicationDataAPI';
import { VendorDataAPI } from '../../API/VendorDataAPI';
import { AssetDataAPI } from '../../API/AssetDataAPI';
import { UsStatesDataAPI } from '../../API/UsStatesDataAPI';
import { DocsDataAPI } from '../../API/DocsDataAPI';
import { ChateauxAPI } from '../../API/Chateaux';

const Assets = ({ app, isReadOnly }) => {
	const { addError, setLoader } = useAPIError();
	const [assets, setAssets] = useState([]);
	const [assetDocuments, setAssetDocuments] = useState([]);
	const [fees, setFees] = useState([]);
	const [vendors, setVendors] = useState([]);
	const [locations, setLocations] = useState([]);
	const [stateSalesTax, setStateSalesTax] = useState({});
	const [usStates, setUsStates] = useState([]);
	const [assetTypes, setAssetTypes] = useState([]);
	const [addAssetModalOpen, setAddAssetModalOpen] = useState(false);
	const [isLoading, setLoading] = useState(false);
	const [isRefresh, setIsRefresh] = useState(false);
	const [feeTypes, setFeeTypeList] = useState([]);

	useEffect(() => {
		(async () => {
			if (app && app.id)  {
				setLoading(true);
				try {
					const response = await Promise.allSettled([
						ApplicationDataAPI.fetchApplicationAssetsById(app.id, app.broker_id, app.locked_by_email),
						ApplicationDataAPI.getApplicationFeesById(app.id, app.broker_id, app.locked_by_email),
						ApplicationDataAPI.getApplicationStateSalesTaxById(app.id, app.broker_id, app.locked_by_email),
						ApplicationDataAPI.fetchApplicationLocationsById(app.id, app.broker_id, app.locked_by_email),
						UsStatesDataAPI.getUsStates(),
						AssetDataAPI.getAssetTypes({ active: true }),
						VendorDataAPI.getAllVendors(0),
						ApplicationDataAPI.fetchApplicationDocumentsById(
							app.id,
							app.broker_id,
							app.locked_by_email
						)
					]);

					// console.log("Asset reponse: ", response);

					const assetResponse =
						response[0].status === "fulfilled" ? response[0].value : [];
					const feesResponse =
						response[1].status === "fulfilled" ? response[1].value : [];
					const appSalesTaxResponse =
						response[2].status === "fulfilled" ? response[2].value : [];
					const appLocationResponse =
						response[3].status === "fulfilled" ? response[3].value : [];
					const usStateResponse =
						response[4].status === "fulfilled" ? response[4].value : {};
					const assetTypesResponse =
						response[5].status === "fulfilled" ? response[5].value : [];
					const vendorResponse =
						response[6].status === "fulfilled" ? response[6].value : [];
					const documentResponse =
						response[7].status === "fulfilled"
							? response[7].value?.filter(
								(x) => x.type === "Asset - Evidence" && x.asset !== undefined
							)
							: [];

					setAssets(assetResponse);
					setAssetDocuments(documentResponse);
					setFees(feesResponse);
					if (appSalesTaxResponse && appSalesTaxResponse.length)
						setStateSalesTax(appSalesTaxResponse[0]);
					setLocations(appLocationResponse);
					setUsStates(usStateResponse);
					setAssetTypes(assetTypesResponse);
					setVendors(vendorResponse);

					const errors = [];
					response.map((item) => {
						if (item.status !== "fulfilled") errors.push(item.reason.message);
						return item;
					});

					if (errors.length > 0) addError(errors);
				} catch (error) {
					addError(error.message);
				} finally {
					setLoading(false);
					setIsRefresh(false);
				}
			}
		})();
	}, [app, isRefresh, addError]);

	const onAssetAdd = async (data) => {
		try {
			setLoader(true);
			await AssetDataAPI.addAsset(data);
			const assetsData = await ApplicationDataAPI.fetchApplicationAssetsById(app.id, app.broker_id, app.locked_by_email);
			// setAssets([...assets, { ...data, id: assetId }]);
			setAssets(assetsData);
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const onAssetUpdate = async (data, attachment) => {
		try {
			setLoader(true);
			setAssets(
				assets.map((item) => (item.id === data.id ? { ...item, ...data } : item))
			);
			if (attachment && Object.keys(attachment)?.length !== 0) {
				await DocumentUpload(attachment);
			}
			if (data.information_source === undefined || data.information_source == "") {
				data.information_source = "Truck Blue Book";
			}
			let isIncomplete = data?.is_incomplete;
			if (data?.is_incomplete) {
				if (data.is_trade_in || data.is_collateral) {
					isIncomplete = false;
				} else if (
					!data.override_year ||
					!data.override_make ||
					!data.override_model
				)
					isIncomplete = true;
				else if (
					data.override_hours == 0 &&
					data.override_mileage == 0 &&
					data.primary_condition == "Used"
				) {
					isIncomplete = true;
				} else {
					isIncomplete = false;
				}
			}
			data.is_incomplete = isIncomplete;
			//these fields are removed otherwise updateAsset API fails
			delete data.inspection_location_address;
			delete data.override_asset_type_gps_fee;
			delete data.override_asset_type_group;
			delete data.override_asset_type_risk_group;
			delete data.override_truck_body_wholesale_price;
			delete data.Asset_Location_Address;
			delete data.vendor;
			delete data.master_vendor;
			delete data.primary_year;
			await AssetDataAPI.updateAsset(data);
			setIsRefresh(true);
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const updateAssets = (newAssets) => {
		setAssets(newAssets);
	};
	const onAssetDuplicate = async (data) => {
		try {
			setLoader(true);

			if (data.information_source === undefined || data.information_source == "") {
				data.information_source = "Truck Blue Book";
			}
			let isIncomplete = data?.is_incomplete;
			if (data?.is_incomplete) {
				if (data.is_trade_in || data.is_collateral) {
					isIncomplete = false;
				} else if (
					!data.override_year ||
					!data.override_make ||
					!data.override_model
				)
					isIncomplete = true;
				else if (
					data.override_hours == 0 &&
					data.override_mileage == 0 &&
					data.primary_condition == "Used"
				) {
					isIncomplete = true;
				} else {
					isIncomplete = false;
				}
			}
			data.is_incomplete = isIncomplete;
			//these fields are removed otherwise updateAsset API fails
			delete data.inspection_location_address;
			delete data.override_asset_type_gps_fee;
			delete data.override_asset_type_group;
			delete data.override_asset_type_risk_group;
			delete data.override_truck_body_wholesale_price;
			delete data.Asset_Location_Address;
			delete data.vendor;
			delete data.master_vendor;
			delete data.primary_year;
			await AssetDataAPI.addAsset(data);
			setIsRefresh(true);
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};


	const onChangeAsset = async (data, attachment) => {
		try {
			setLoader(true);
			setAssets(
				assets.map((item) => (item.id === data.id ? { ...item, ...data } : item))
			);
			if (data.is_location == false || data.is_location == undefined) {
				data.vendor = data.vendor_id;
			} else delete data.vendor;
			delete data.is_location;
			delete data.vendor_id;
			if (attachment && Object.keys(attachment)?.length !== 0) {
				await DocumentUpload(attachment);
			}
			if (data.information_source === undefined || data.information_source == "") {
				data.information_source = "Truck Blue Book";
			}
			//these fields are removed otherwise updateAsset API fails
			delete data.override_asset_type_gps_fee;
			delete data.override_asset_type_group;
			delete data.override_asset_type_risk_group;
			delete data.override_truck_body_wholesale_price;
			delete data.Asset_Location_Address;
			delete data.master_vendor;
			delete data.primary_year;
			await AssetDataAPI.updateAsset(data);
			setIsRefresh(true);
		} catch (error) {
			addError(error.message);
			setLoader(false);
		}
	};

	const DocumentUpload = async (data) => {
		try {
			let DocumentId = "";
			if (Object.keys(data)?.length === 0) {
				return;
			}
			if (data?.application_id === undefined || data?.application_id === null) {
				return;
			}
			if (Object.keys(data)?.length !== 0) {
				var now = new Date();
				var isoString = now.toISOString();
				const documentInfo = {
					application_id: data.application_id,
					asset: data.asset,
					document_name: data.document_name,
					date_uploaded: isoString,
					type: data.type,
					related_to: "Lessee",
					notes: "Test",
				};
				let documentUp = await DocsDataAPI.postDocument(documentInfo, app?.broker_id, app?.locked_by_email);
				const attachmentInfo = {
					name: data.document_name,
					parent_id: documentUp,
					content_type: data.content_type,
					body: data.base_file,
					private: false,
				};
				await DocsDataAPI.postAttachment(attachmentInfo);
				DocumentId = documentUp;
			}
			return DocumentId;
		} catch (error) {
			addError(error.message);
		}
	};

	const onAssetDelete = async (assetId) => {
		try {
			setLoader(true);
			await AssetDataAPI.deleteAsset(assetId);
			setAssets(assets.filter((asset) => asset.id !== assetId));
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};
	const getAssetDocuments = async (assetId) => {
		try {
			setLoader(true);
			await AssetDataAPI.getAssetDocuments(assetId);
			setAssets(assets.filter((asset) => asset.id !== assetId));
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const onFeeUpdate = async (data) => {
		try {
			setLoader(true);
			setFees(
				fees.map((item) => (item.id === data.id ? { ...item, ...data } : item))
			);
			await FeeDataAPI.updateFee(data);
			setIsRefresh(true);
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const onFeeAdd = async (data) => {
		try {
			setLoader(true);
			await FeeDataAPI.addFee(data);
			//setFees([...fees, { ...data, id: feeId }]);
			const fees = await ApplicationDataAPI.getApplicationFeesById(app.id, app.broker_id, app.locked_by_email);
			setFees(fees);
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const onFeeDelete = async (feeId) => {
		try {
			setLoader(true);
			await FeeDataAPI.deleteFee(feeId);
			setFees(fees.filter((fee) => fee.id !== feeId));
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const onVendorAdd = async (data) => {
		try {
			setLoader(true);
			const vendorId = await VendorDataAPI.addVendor(data);
			setVendors([...vendors, { ...data, id: vendorId }]);
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const getVendors = async (name, offset) => {
		const response = await VendorDataAPI.getVendors(name, offset);
		return response;
	};

	const getAttachment = async (documentId) => {
		try {
			let Attachment = await DocsDataAPI.getDocumentAttachment(documentId);
			return Attachment;
		} catch (error) {
			console.log(error.message);
		}
	};

	const getVendor = async (vendorId) => {
		const response = await VendorDataAPI.getVendor(vendorId);
		return response;
	};

	const getPDConfig = async (data) => {
		const response = await ChateauxAPI.getPriceDigestConfig(data);
		return response;
	};

	const getPDValue = async (data) => {
		const response = await ChateauxAPI.getPriceDigestValue(data);
		return response;
	};

	const getEWConfig = async (data) => {
		const response = await ChateauxAPI.getEquipmentWatchConfig(data);
		return response;
	};

	const getEWValue = async (data) => {
		const response = await ChateauxAPI.getEquipmentWatchValue(data);
		return response;
	};

	return (
		<AssetContext.Provider
			value={{
				isReadOnly: isReadOnly,
				app: app,
				assets: assets,
				assetDocuments: assetDocuments,
				fees: fees,
				vendors: vendors,
				locations: locations,
				stateSalesTax: stateSalesTax,
				usStates: usStates,
				assetTypes: assetTypes,
				feeTypes: feeTypes,
				onAssetAdd,
				onAssetUpdate,
				onAssetDuplicate,
				updateAssets,
				onChangeAsset,
				onAssetDelete,
				onFeeUpdate,
				onFeeAdd,
				onFeeDelete,
				onVendorAdd,
				getVendor,
				getVendors,
				getPDConfig,
				getPDValue,
				getEWConfig,
				getEWValue,
				getAttachment,
				getAssetDocuments,
				setFeeTypeList
			}}>
			{isLoading ? (
				<div style={{ padding: 20 }}>
					<NmefSpinner />
				</div>
			) : (
				!isRefresh && (
					<>
						<Asset
							addAssetModalOpen={addAssetModalOpen}
							setAddAssetModalOpen={setAddAssetModalOpen}
						/>
					</>
				)
			)}
		</AssetContext.Provider>
	);
};
export default Assets;
